import useSWR from 'swr';

import { logger } from 'src/logger';
import { swrFetcher } from 'src/utils/swr-helpers';

export interface UserRightsResponse {
  userRights: string[];
}

export interface UserRightsContext {
  /** Array of user rights/permissions */
  userRights: string[];
  /** Whether the user is authenticated (has 'AUTHENTICATED' right) */
  isAuthenticated: boolean;
  isLoading: boolean;
  error: any;
}

/**
 * Custom hook to fetch and manage user rights from the AsxCustomerProfileService (ACPS).
 *
 * This hook retrieves user permissions and authentication status by calling the ACPS
 * getUserRights endpoint. It uses SWR for data fetching with specific configuration
 * to ensure fresh data on every page load and proper error handling.
 *
 * @see {@link https://code.amazon.com/packages/AsxCustomerProfileService/blobs/mainline/--/src/main/java/com/amazon/asxcustomerprofileservice/activity/userrights/GetUserRightsActivity.java ACPS GetUserRightsActivity}
 *
 * @returns {UserRightsContext} Object containing user rights, authentication status, loading state, and any errors
 *
 * @example
 * ```tsx
 * const { userRights, isAuthenticated, isLoading, error } = useGetUserRights();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error loading user rights</div>;
 *
 * return (
 *   <div>
 *     {isAuthenticated ? 'User is authenticated' : 'User is not authenticated'}
 *     <ul>
 *       {userRights.map(right => <li key={right}>{right}</li>)}
 *     </ul>
 *   </div>
 * );
 * ```
 */
export const useGetUserRights = (): UserRightsContext => {
  // Use useSWR (not useSWRImmutable) to ensure fresh data on every page load
  // and disable caching to always fetch user rights
  const { data, error, isLoading } = useSWR<UserRightsResponse>('/acps/getUserRights', swrFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 0, // Don't auto-refresh
    dedupingInterval: 0, // Don't dedupe requests
    errorRetryCount: 1, // Retry once on error
  });

  if (error) {
    logger.error('Failed to load user rights from API.', error);
  }

  const userRights = data?.userRights || [];
  const isAuthenticated = userRights.includes('AUTHENTICATED');

  return {
    userRights,
    isAuthenticated,
    isLoading,
    error,
  };
};
